<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { FileUp, Loader2, CheckCheck } from 'lucide-vue-next';
import { useFileUpload } from '@/components/upload-file/useFileUpload';
import {
    FormField,
    FormItem,
    FormControl,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { useRoute } from 'vue-router';

const route = useRoute();
const numberOfUploadedFiles = ref('0');

const { fileNames, fileLoader, uploadFile } = useFileUpload({ appID: route.query.appID as string});

function clickAddFile() {
    const input: HTMLElement = document.querySelector('.upload-file-input') as HTMLElement;
    input?.click();
}

const uploadedFilesNames = computed<string[]>((prev) => {
    if (typeof prev === 'undefined') {
        return fileNames.value;
    }

    return Array.from(new Set([...fileNames.value, ...prev]));
})


onMounted(() => {
    axios.post('/numberOfUploadedFiles', {
        appID: route.query.appID,
    }).then(
        (res) => {
            numberOfUploadedFiles.value = res.data;
        }
    );
})
</script>

<template>
    <div>
        <h2 class="title">What Would You Like to Share With Your Doctor?</h2>
        <div class="mt-1 text-gray-600">Send images, test results, or documents that may help the doctor understand your
            condition better.</div>
        <div class="mt-8 flex flex-col gap-5">
            <Button size="lg" @click="clickAddFile" variant="default" class="!text-lg mt-2 font-semibold"
                :disabled="fileLoader">
                <Loader2 v-if="fileLoader" class="size-5 mr-3 animate-spin" />
                <FileUp v-else class="size-5 mr-3" />
                Add File
            </Button>
            <div class="text-center text-gray-600 font-medium transition-all duration-100"
                v-if="Number(numberOfUploadedFiles) > 0 && uploadedFilesNames.length === 0">
                You have added {{ numberOfUploadedFiles }} files before
            </div>
            <form class="hidden">
                <FormField name="file">
                    <FormItem>
                        <FormControl>
                            <Input type="file" multiple accept=".jpg, .jpeg, .pdf, .png"
                                class="hidden upload-file-input" @change="uploadFile" />
                        </FormControl>
                        <FormMessage name="uploadedFiles" />
                    </FormItem>
                </FormField>
            </form>
            <div class="flex flex-col gap-3 justify-start w-full">
                <TransitionGroup name="upload-list">
                    <div v-for="file in uploadedFilesNames" :key="file"
                        class="border border-gray-300 bg-white rounded-xl p-4 w-full break-normal truncate flex flex-row items-center justify-stretch gap-2 font-medium">
                        <span class="truncate grow">{{ file }}</span>
                        <CheckCheck class="size-6 shrink-0 text-green-600 ml-1" />
                    </div>
                </TransitionGroup>
            </div>
        </div>
    </div>
</template>
